/**
 * Classroom Tools Slice
 *
 * Manages state for classroom management widgets:
 * - Countdown Timer
 * - Work Symbols
 * - Traffic Light
 * - Stopwatch
 */

import type { StateCreator } from 'zustand';
import type { AppStore } from '../types';

// ============================================================================
// Types
// ============================================================================

/** Timer preset options (in seconds) */
export type TimerPreset = 60 | 120 | 300 | 600 | 900 | 1200;

/** Timer preset labels for UI */
export const TIMER_PRESETS: { value: TimerPreset; label: string }[] = [
  { value: 60, label: '1m' },
  { value: 120, label: '2m' },
  { value: 300, label: '5m' },
  { value: 600, label: '10m' },
  { value: 900, label: '15m' },
  { value: 1200, label: '20m' },
];

/** Work symbol types */
export type WorkSymbol = 'silent' | 'whisper' | 'neighbor' | 'collaborate' | 'raise-hand' | null;

/** Work symbol definitions for UI */
export const WORK_SYMBOLS: {
  id: Exclude<WorkSymbol, null>;
  icon: string;
  label: string;
  color: string;
  bgColor: string;
}[] = [
  { id: 'silent', icon: '🤫', label: 'Silent Work', color: 'text-red-700', bgColor: 'bg-red-100' },
  { id: 'whisper', icon: '🗣️', label: 'Whisper Voice', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  { id: 'neighbor', icon: '👋', label: 'Ask Neighbor', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  { id: 'collaborate', icon: '🤝', label: 'Collaborate', color: 'text-green-700', bgColor: 'bg-green-100' },
  { id: 'raise-hand', icon: '✋', label: 'Raise Hand', color: 'text-purple-700', bgColor: 'bg-purple-100' },
];

/** Traffic light states */
export type TrafficLightState = 'red' | 'yellow' | 'green';

/** Volume level definition for a single light */
export interface VolumeLevelDef {
  label: string;
  activeColor: string;
  inactiveColor: string;
  glowColor: string;
}

/** Default volume level definitions */
export const DEFAULT_VOLUME_LEVELS: Record<TrafficLightState, VolumeLevelDef> = {
  red: {
    label: 'Silent',
    activeColor: 'bg-red-500',
    inactiveColor: 'bg-red-900/30',
    glowColor: 'shadow-red-500/60',
  },
  yellow: {
    label: 'Whisper',
    activeColor: 'bg-yellow-400',
    inactiveColor: 'bg-yellow-800/20',
    glowColor: 'shadow-yellow-400/60',
  },
  green: {
    label: 'Partner Talk',
    activeColor: 'bg-teal-400',
    inactiveColor: 'bg-teal-900/20',
    glowColor: 'shadow-teal-400/60',
  },
};

/** Traffic light color definitions (kept for backward compat) */
export const TRAFFIC_LIGHT_COLORS: Record<
  TrafficLightState,
  { active: string; inactive: string; glow: string }
> = {
  red: {
    active: DEFAULT_VOLUME_LEVELS.red.activeColor,
    inactive: DEFAULT_VOLUME_LEVELS.red.inactiveColor,
    glow: DEFAULT_VOLUME_LEVELS.red.glowColor,
  },
  yellow: {
    active: DEFAULT_VOLUME_LEVELS.yellow.activeColor,
    inactive: DEFAULT_VOLUME_LEVELS.yellow.inactiveColor,
    glow: DEFAULT_VOLUME_LEVELS.yellow.glowColor,
  },
  green: {
    active: DEFAULT_VOLUME_LEVELS.green.activeColor,
    inactive: DEFAULT_VOLUME_LEVELS.green.inactiveColor,
    glow: DEFAULT_VOLUME_LEVELS.green.glowColor,
  },
};

/** Lap record for stopwatch */
export interface LapRecord {
  id: string;
  lapNumber: number;
  /** Elapsed time in milliseconds when lap was recorded */
  time: number;
  /** Delta from previous lap in milliseconds */
  delta: number;
}

// ============================================================================
// State & Actions
// ============================================================================

export interface ClassroomToolsState {
  // Countdown Timer
  /** Target duration in seconds */
  timerDuration: number;
  /** Remaining time in seconds */
  timerRemaining: number;
  /** Whether timer is currently running */
  timerIsRunning: boolean;
  /** Timestamp (ms) when timer was started (for persistence across panel close) */
  timerStartedAt: number | null;
  /** Whether to play chime on completion */
  timerChimeEnabled: boolean;
  /** Whether timer has completed (for triggering chime once) */
  timerCompleted: boolean;

  // Work Symbols
  /** Currently active work symbol (null if none) */
  activeSymbol: WorkSymbol;

  // Traffic Light
  /** Current traffic light state */
  trafficLightState: TrafficLightState;

  // Stopwatch
  /** Elapsed time in milliseconds */
  stopwatchElapsed: number;
  /** Whether stopwatch is currently running */
  stopwatchIsRunning: boolean;
  /** Timestamp (ms) when stopwatch was started (for persistence) */
  stopwatchStartedAt: number | null;
  /** Recorded laps */
  stopwatchLaps: LapRecord[];
}

export interface ClassroomToolsActions {
  // Countdown Timer
  /** Set timer duration (also resets remaining time) */
  setTimerDuration: (seconds: number) => void;
  /** Set timer to a preset value */
  setTimerPreset: (preset: TimerPreset) => void;
  /** Start the timer */
  startTimer: () => void;
  /** Pause the timer */
  pauseTimer: () => void;
  /** Reset the timer to its duration */
  resetTimer: () => void;
  /** Tick the timer (called by interval) */
  tickTimer: () => void;
  /** Sync timer with elapsed time since start (for panel reopen) */
  syncTimer: () => void;
  /** Enable/disable completion chime */
  setTimerChimeEnabled: (enabled: boolean) => void;
  /** Mark timer as acknowledged after completion */
  acknowledgeTimerCompletion: () => void;

  // Work Symbols
  /** Set the active work symbol */
  setActiveSymbol: (symbol: WorkSymbol) => void;
  /** Toggle a symbol (select if not active, clear if active) */
  toggleSymbol: (symbol: Exclude<WorkSymbol, null>) => void;
  /** Clear the active symbol */
  clearSymbol: () => void;

  // Traffic Light
  /** Set the traffic light state */
  setTrafficLight: (state: TrafficLightState) => void;
  /** Cycle to the next traffic light state (green -> yellow -> red -> green) */
  cycleTrafficLight: () => void;

  // Stopwatch
  /** Start the stopwatch */
  startStopwatch: () => void;
  /** Stop the stopwatch */
  stopStopwatch: () => void;
  /** Reset the stopwatch and clear laps */
  resetStopwatch: () => void;
  /** Record a lap */
  recordLap: () => void;
  /** Clear all laps */
  clearLaps: () => void;
  /** Tick the stopwatch (called by interval) */
  tickStopwatch: () => void;
  /** Sync stopwatch with elapsed time since start (for panel reopen) */
  syncStopwatch: () => void;
}

export type ClassroomToolsSlice = ClassroomToolsState & ClassroomToolsActions;

// ============================================================================
// Initial State
// ============================================================================

const DEFAULT_TIMER_DURATION = 300; // 5 minutes

const initialClassroomToolsState: ClassroomToolsState = {
  // Countdown Timer
  timerDuration: DEFAULT_TIMER_DURATION,
  timerRemaining: DEFAULT_TIMER_DURATION,
  timerIsRunning: false,
  timerStartedAt: null,
  timerChimeEnabled: true,
  timerCompleted: false,

  // Work Symbols
  activeSymbol: null,

  // Traffic Light
  trafficLightState: 'green',

  // Stopwatch
  stopwatchElapsed: 0,
  stopwatchIsRunning: false,
  stopwatchStartedAt: null,
  stopwatchLaps: [],
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createClassroomToolsSlice: StateCreator<
  AppStore,
  [],
  [],
  ClassroomToolsSlice
> = (set, get) => ({
  ...initialClassroomToolsState,

  // -------------------------------------------------------------------------
  // Countdown Timer Actions
  // -------------------------------------------------------------------------

  setTimerDuration: (seconds) => {
    set({
      timerDuration: seconds,
      timerRemaining: seconds,
      timerIsRunning: false,
      timerStartedAt: null,
      timerCompleted: false,
    });
  },

  setTimerPreset: (preset) => {
    set({
      timerDuration: preset,
      timerRemaining: preset,
      timerIsRunning: false,
      timerStartedAt: null,
      timerCompleted: false,
    });
  },

  startTimer: () => {
    const state = get();
    if (state.timerRemaining <= 0) return;

    set({
      timerIsRunning: true,
      timerStartedAt: Date.now(),
      timerCompleted: false,
    });
  },

  pauseTimer: () => {
    const state = get();
    if (!state.timerIsRunning) return;

    // Calculate actual remaining time
    const elapsed = state.timerStartedAt
      ? (Date.now() - state.timerStartedAt) / 1000
      : 0;
    const remaining = Math.max(0, state.timerRemaining - elapsed);

    set({
      timerIsRunning: false,
      timerRemaining: remaining,
      timerStartedAt: null,
    });
  },

  resetTimer: () => {
    const state = get();
    set({
      timerRemaining: state.timerDuration,
      timerIsRunning: false,
      timerStartedAt: null,
      timerCompleted: false,
    });
  },

  tickTimer: () => {
    const state = get();
    if (!state.timerIsRunning || !state.timerStartedAt) return;

    const elapsed = (Date.now() - state.timerStartedAt) / 1000;
    const remaining = Math.max(0, state.timerRemaining - elapsed);

    if (remaining <= 0) {
      set({
        timerRemaining: 0,
        timerIsRunning: false,
        timerStartedAt: null,
        timerCompleted: true,
      });
    }
  },

  syncTimer: () => {
    const state = get();
    if (!state.timerIsRunning || !state.timerStartedAt) return;

    const elapsed = (Date.now() - state.timerStartedAt) / 1000;
    const remaining = Math.max(0, state.timerRemaining - elapsed);

    if (remaining <= 0) {
      set({
        timerRemaining: 0,
        timerIsRunning: false,
        timerStartedAt: null,
        timerCompleted: true,
      });
    } else {
      // Update startedAt to now and adjust remaining
      set({
        timerRemaining: remaining,
        timerStartedAt: Date.now(),
      });
    }
  },

  setTimerChimeEnabled: (enabled) => {
    set({ timerChimeEnabled: enabled });
  },

  acknowledgeTimerCompletion: () => {
    set({ timerCompleted: false });
  },

  // -------------------------------------------------------------------------
  // Work Symbols Actions
  // -------------------------------------------------------------------------

  setActiveSymbol: (symbol) => {
    set({ activeSymbol: symbol });
  },

  toggleSymbol: (symbol) => {
    const state = get();
    set({ activeSymbol: state.activeSymbol === symbol ? null : symbol });
  },

  clearSymbol: () => {
    set({ activeSymbol: null });
  },

  // -------------------------------------------------------------------------
  // Traffic Light Actions
  // -------------------------------------------------------------------------

  setTrafficLight: (state) => {
    set({ trafficLightState: state });
  },

  cycleTrafficLight: () => {
    const state = get();
    const cycle: TrafficLightState[] = ['green', 'yellow', 'red'];
    const currentIndex = cycle.indexOf(state.trafficLightState);
    const nextIndex = (currentIndex + 1) % cycle.length;
    set({ trafficLightState: cycle[nextIndex] });
  },

  // -------------------------------------------------------------------------
  // Stopwatch Actions
  // -------------------------------------------------------------------------

  startStopwatch: () => {
    set({
      stopwatchIsRunning: true,
      stopwatchStartedAt: Date.now(),
    });
  },

  stopStopwatch: () => {
    const state = get();
    if (!state.stopwatchIsRunning) return;

    // Calculate actual elapsed time
    const additionalElapsed = state.stopwatchStartedAt
      ? Date.now() - state.stopwatchStartedAt
      : 0;

    set({
      stopwatchIsRunning: false,
      stopwatchElapsed: state.stopwatchElapsed + additionalElapsed,
      stopwatchStartedAt: null,
    });
  },

  resetStopwatch: () => {
    set({
      stopwatchElapsed: 0,
      stopwatchIsRunning: false,
      stopwatchStartedAt: null,
      stopwatchLaps: [],
    });
  },

  recordLap: () => {
    const state = get();
    const currentElapsed = state.stopwatchIsRunning && state.stopwatchStartedAt
      ? state.stopwatchElapsed + (Date.now() - state.stopwatchStartedAt)
      : state.stopwatchElapsed;

    const lastLap = state.stopwatchLaps[state.stopwatchLaps.length - 1];
    const delta = lastLap ? currentElapsed - lastLap.time : currentElapsed;

    const newLap: LapRecord = {
      id: crypto.randomUUID(),
      lapNumber: state.stopwatchLaps.length + 1,
      time: currentElapsed,
      delta,
    };

    set({
      stopwatchLaps: [...state.stopwatchLaps, newLap],
    });
  },

  clearLaps: () => {
    set({ stopwatchLaps: [] });
  },

  tickStopwatch: () => {
    // Stopwatch doesn't need to tick - it calculates elapsed on render
    // This is a no-op but kept for API consistency with timer
  },

  syncStopwatch: () => {
    const state = get();
    if (!state.stopwatchIsRunning || !state.stopwatchStartedAt) return;

    // Stopwatch continues from where it was - no action needed
    // The elapsed time is calculated dynamically in components
  },
});
